import { useEffect, useState, useCallback, useMemo } from 'react';
import { addWeeks, startOfDay } from 'date-fns';
import { useCommunication } from '../lib/CommunicationWrapper';

interface MicrosoftCalendarEvent {
	id: string;
	subject: string;
	bodyPreview?: string;
	body?: {
		contentType: string;
		content: string;
	};
	start: {
		dateTime: string;
		timeZone: string;
	};
	end: {
		dateTime: string;
		timeZone: string;
	};
	location?: {
		displayName: string;
		address?: any;
	};
	webLink?: string;
	isAllDay?: boolean;
	isCancelled?: boolean;
	organizer?: {
		emailAddress?: {
			name?: string;
			address?: string;
		};
	};
	attendees?: Array<{
		emailAddress?: {
			name?: string;
			address?: string;
		};
		type?: string;
		status?: {
			response?: string;
		};
	}>;
	categories?: string[];
	recurrence?: any;
	seriesMasterId?: string;
	calendarId?: string;
	calendarColor?: string;
}

// Convert Microsoft event format to match Google Calendar event format for compatibility
interface NormalizedCalendarEvent {
	id: string;
	summary: string;
	description?: string;
	start: {
		dateTime?: string;
		date?: string;
		timeZone?: string;
	};
	end: {
		dateTime?: string;
		date?: string;
		timeZone?: string;
	};
	colorId?: string;
	htmlLink?: string;
	status?: string;
	organizer?: {
		email?: string;
		displayName?: string;
	};
	attendees?: any[];
	location?: string;
	recurringEventId?: string;
	calendarId?: string;
	calendarColor?: string;
}

export const useMicrosoftCalendarEvents = () => {
	const [events, setEvents] = useState<NormalizedCalendarEvent[]>([]);
	const [lastSync, setLastSync] = useState<number | null>(null);

    const { useRerenderListener, sendMessageAndListen, authenticatedClient, updateOptions } = useCommunication();

	// Watch for changes to cached events in local storage
	const { options } = useRerenderListener(['microsoft_cal_events', 'microsoft_cal_events_last_sync'], 'local');

	// Also watch for token and calendars from sync storage for fetchEventsForDateRange
	const { options: syncOptions } = useRerenderListener(['microsoft_calendars', 'microsoft_cal_sync_token'], 'sync');

	// Convert Microsoft event to normalized format (matching Google Calendar structure)
	const normalizeMicrosoftEvent = (event: MicrosoftCalendarEvent, calendarId: string, calendarColor: string): NormalizedCalendarEvent => {
		const isAllDay = event.isAllDay || false;

		// Microsoft Graph API returns UTC times, we need to convert them to ISO format with timezone info
		// If the timezone is UTC, we need to add the Z suffix for proper parsing
		const convertDateTime = (dateTime: string, timeZone: string): string => {
			// Microsoft returns format like "2025-10-15T13:00:00.0000000"
			// Remove the fractional seconds (.0000000)
			const cleanDateTime = dateTime.replace(/\.\d{7}$/, '');

			// If timezone is UTC and not all-day, add 'Z' to indicate UTC
			if (timeZone === 'UTC' && !isAllDay) {
				return cleanDateTime + 'Z';
			}

			// For non-UTC timezones or all-day events, return as-is
			// The Date constructor will handle it based on the timeZone property
			return cleanDateTime;
		};

		const startDateTime = convertDateTime(event.start.dateTime, event.start.timeZone);
		const endDateTime = convertDateTime(event.end.dateTime, event.end.timeZone);

		return {
			id: event.id,
			summary: event.subject || 'Untitled Event',
			description: event.bodyPreview || event.body?.content,
			start: isAllDay ? {
				date: startDateTime.split('T')[0],
				timeZone: event.start.timeZone
			} : {
				dateTime: startDateTime,
				timeZone: event.start.timeZone
			},
			end: isAllDay ? {
				date: endDateTime.split('T')[0],
				timeZone: event.end.timeZone
			} : {
				dateTime: endDateTime,
				timeZone: event.end.timeZone
			},
			htmlLink: event.webLink,
			status: event.isCancelled ? 'cancelled' : 'confirmed',
			organizer: event.organizer ? {
				email: event.organizer.emailAddress?.address,
				displayName: event.organizer.emailAddress?.name
			} : undefined,
			attendees: event.attendees?.map(attendee => ({
				email: attendee.emailAddress?.address,
				displayName: attendee.emailAddress?.name,
				responseStatus: attendee.status?.response
			})),
			location: event.location?.displayName,
			recurringEventId: event.seriesMasterId,
			calendarId,
			calendarColor
		};
	};

	// Process cached events from local storage
	useEffect(() => {
		const cachedEvents = options?.microsoft_cal_events || {};
		const syncTimestamp = options?.microsoft_cal_events_last_sync || null;

		console.log('[useMicrosoftCalendarEvents] Processing cached events from local storage');

		const allEvents: NormalizedCalendarEvent[] = [];

		// Flatten events from all calendars
		for (const calendarEvents of Object.values(cachedEvents)) {
			if (Array.isArray(calendarEvents)) {
				allEvents.push(...calendarEvents);
			}
		}

		// Sort events by start time
		allEvents.sort((a, b) => {
			const aStart = new Date(a.start?.dateTime || a.start?.date || 0);
			const bStart = new Date(b.start?.dateTime || b.start?.date || 0);
			return aStart.getTime() - bStart.getTime();
		});

		console.log(`[useMicrosoftCalendarEvents] Loaded ${allEvents.length} events from cache`);

		setEvents(allEvents);
		setLastSync(syncTimestamp);
	}, [options?.microsoft_cal_events, options?.microsoft_cal_events_last_sync]);

	// Helper to check and refresh token (calls extension's checkAndRefreshToken via message)
	const getValidToken = useCallback(async () => {
		return await sendMessageAndListen({
			type: "CALL_EXTENSION_FUNC",
			payload: {
				func: "checkAndRefreshCalendarTokens",
				args: {}
			}
		});
	}, [sendMessageAndListen]);

	// Fetch events for a specific calendar
	const fetchCalendarEvents = useCallback(async (calendarId: string, timeMin: Date, timeMax: Date) => {
		const validToken = await getValidToken();
		if (!validToken) {
			console.error('[useMicrosoftCalendarEvents] No valid token available');
			return [];
		}

		try {
			const url = new URL(`https://graph.microsoft.com/v1.0/me/calendars/${calendarId}/calendarview`);
			url.searchParams.append('startDateTime', timeMin.toISOString());
			url.searchParams.append('endDateTime', timeMax.toISOString());
			url.searchParams.append('$top', '250');
			url.searchParams.append('$orderby', 'start/dateTime');
			url.searchParams.append('$select', 'id,subject,bodyPreview,body,start,end,location,webLink,isAllDay,isCancelled,organizer,attendees,categories,recurrence,seriesMasterId');

			console.log(`[useMicrosoftCalendarEvents] Fetching from: ${url.toString()}`);

			const response = await fetch(url.toString(), {
				headers: {
					Authorization: `Bearer ${validToken.access_token}`,
					'Content-Type': 'application/json',
				},
			});

			if (!response.ok) {
				console.error(`[useMicrosoftCalendarEvents] Failed to fetch calendar ${calendarId}: ${response.status}`);
				return [];
			}

			const data = await response.json();
			console.log(`[useMicrosoftCalendarEvents] Fetched ${data.value?.length || 0} events for calendar ${calendarId}`);

			// Filter out Canvas-pushed events (check categories for BetterCanvas)
			return (data.value || []).filter((event: any) => {
				const isCanvasPushed = event.categories?.includes('BetterCanvas');
				return !isCanvasPushed && !event.isCancelled;
			});
		} catch (error) {
			console.error(`[useMicrosoftCalendarEvents] Error fetching calendar ${calendarId}:`, error);
			return [];
		}
	}, [getValidToken]);

	// Helper function to check and refresh Microsoft token
	const checkAndRefreshToken = async (currentOptions: any) => {
		const token = currentOptions?.microsoft_cal_sync_token;

		if (!token) return null;

		const now = Date.now();
		// Skip refresh if the token isn't expired (with 5 minute buffer)
		if (token.expires_at && now <= token.expires_at - 5 * 60 * 1000) return token;

		console.log('[useMicrosoftCalendarEvents] Token expired, refreshing...');

		try {
			const client = await authenticatedClient();
			const { data, error } = await client.functions.invoke('microsoft-cal-refresh', {
				body: {
					refresh_token: token.refresh_token,
				},
			});

			if (error) {
				throw new Error(error.message || 'Failed to refresh token');
			}

			// Update the token in storage
			const newToken = {
				...token,
				access_token: data.message.access_token,
				expires_at: Date.now() + data.message.expires_in * 1000,
			};

			await updateOptions({ "microsoft_cal_sync_token": newToken }, "local");
			return newToken;
		} catch (err) {
			console.error('Error refreshing Microsoft token:', err);
			return null;
		}
	};

	// Function to fetch events for a specific date range (on-demand)
	const fetchEventsForDateRange = useCallback(async (date: Date) => {
		try {
			if (!syncOptions?.microsoft_calendars || !syncOptions?.microsoft_cal_sync_token) {
				return [];
			}

			// Calculate a 1-week range starting from the requested date
			const startDate = startOfDay(date);
			const endDate = addWeeks(startDate, 1);

			console.log(`[useMicrosoftCalendarEvents] Fetching events from ${startDate.toISOString()} to ${endDate.toISOString()}`);

			const allEvents: NormalizedCalendarEvent[] = [];

			// Fetch events for each calendar that should sync
			for (const [calendarId, calendar] of Object.entries(syncOptions.microsoft_calendars)) {
				const cal = calendar as any;
				if (!cal.shouldSync) continue;

				const calendarEvents = await fetchCalendarEvents(calendarId, startDate, endDate);

				// Normalize and add calendar metadata to each event
				const normalizedEvents = calendarEvents.map((event: MicrosoftCalendarEvent) =>
					normalizeMicrosoftEvent(event, calendarId, cal.color || '#0078D4')
				);

				allEvents.push(...normalizedEvents);
			}

			// Sort events by start time
			allEvents.sort((a, b) => {
				const aStart = new Date(a.start?.dateTime || a.start?.date || 0);
				const bStart = new Date(b.start?.dateTime || b.start?.date || 0);
				return aStart.getTime() - bStart.getTime();
			});

			console.log(`[useMicrosoftCalendarEvents] Fetched ${allEvents.length} events for range`);
			return allEvents;
		} catch (error) {
			console.error('[useMicrosoftCalendarEvents] Error fetching date range:', error);
			return [];
		}
	}, [syncOptions?.microsoft_calendars, syncOptions?.microsoft_cal_sync_token, fetchCalendarEvents]);

	// Memoize the return object to prevent unnecessary re-renders
	return useMemo(() => ({
		events,
		loading: false, // Extension handles loading
		lastSync,
		fetchEventsForDateRange,
		refetch: () => { } // Will be implemented via extension
	}), [events, lastSync, fetchEventsForDateRange]);
};

// Helper function to format Microsoft Calendar events for display
export const formatMicrosoftCalendarEvent = (event: NormalizedCalendarEvent) => {
	const startDate = new Date(event.start?.dateTime || event.start?.date || 0);
	const endDate = new Date(event.end?.dateTime || event.end?.date || 0);

	const isAllDay = !event.start?.dateTime;

	return {
		...event,
		startDate,
		endDate,
		isAllDay,
		duration: endDate.getTime() - startDate.getTime(),
	};
};